import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI
import { Box, Flex, Spinner } from '@chakra-ui/react'

// Components

// Hooks
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import { PURCHASE_SCENARIO_SELECT_OPTIONS } from '@/features/GCMaterials/constants/purchaseScenarioOptions'
import { useReason } from '@/features/reasons/useReason'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useUser from '@/features/users/useUser'
import useUserTableProfile from '@/features/userTableProfiles/useUserTableProfile'

// Constants
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { TableType } from '@/features/tableProfiles/tableProfile.enum'

// Helpers

// Slices
import { authSelector } from '@/stores/auth/authSlice'

// Components
import { useSearchParams } from 'react-router-dom'
import MyOfferTable from './MyOfferTable'

const MyOfferPage = () => {
  const [searchParams] = useSearchParams()
  const profileId = searchParams.get('profileId')

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user: currentUser } = useSelector(authSelector)

  const { useFetchAllUsers, useUserOptions } = useUser()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useSystemVariableOptions } = useSystemVariable()
  const { useGetMyTableProfiles } = useUserTableProfile()
  const { useFetchReasonsByType } = useReason()

  const { data: userTableProfile, isLoading: isLoadingTableProfile } = useGetMyTableProfiles(TableType.OFFERS_LIST)

  const { data: salesUserOptions, isLoading: isLoadingUserOptions } = useUserOptions({
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
    status: ['active', 'inactive', 'blocked'],
  })
  const { data: engineerUserOptions, isLoading: isLoadingEngineerUserOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.ENGINEER],
    status: ['active', 'inactive', 'blocked'],
  })
  const { data: allUserOptions, isLoading: isLoadingAllUserOptions } = useUserOptions({
    status: ['active', 'inactive', 'blocked'],
  })
  const { data: PMOptions, isLoading: isLoadingPMOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.SALES],
    status: ['active', 'inactive', 'blocked'],
  })

  const { data: sourcingUserOptions, isLoading: isLoadingSourcingUserOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.PROCUREMENT_OFFICER],
    status: ['active', 'inactive', 'blocked'],
  })
  const { data: teamAssistantOptions, isLoading: isLoadingTeamAssistantOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TEAM_ASSISTANT],
    status: ['active', 'inactive', 'blocked'],
  })

  const { data: teamOptions, isLoading: isLoadingTeamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, {
    status: 'active',
  })

  const { data: businessUnitOptions, isLoading: isLoadingBusinessUnitOptions } = useSystemVariableOptions(
    SystemVariableType.BUSINESS_UNIT,
    { status: ['active', 'inactive'] }
  )

  const { data: costCenterOptions, isLoading: isLoadingCostCenterOptions } = useCostCenterOptions({
    status: ['active', 'inactive'],
    labelType: 'simple',
  })
  const { data: GCKOptions, isLoading: isLoadingGCKOptions } = useGCKCodeOptions()

  const { data: SISICOptions, isLoading: isLoadingSISICOptions } = useSystemVariableOptions(SystemVariableType.SISIC, {
    status: ['active', 'inactive'],
    valueKey: 'code',
    labelKey: (sisic) => `${sisic.code} - ${sisic.name}`,
  })

  const { data: CPMOptions, isLoading: isLoadingCPMOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.COMMERCIAL_PROJECT_MANAGER],
  })

  const { data: fetchReasonResponse, isLoading: isFetchReasonResponse } = useFetchReasonsByType('offer')

  const allReasons = fetchReasonResponse ?? []

  if (
    isLoadingTableProfile ||
    isLoadingUserOptions ||
    isLoadingEngineerUserOptions ||
    isLoadingAllUserOptions ||
    isLoadingPMOptions ||
    isLoadingTeamAssistantOptions ||
    isLoadingTeamOptions ||
    isLoadingBusinessUnitOptions ||
    isLoadingCostCenterOptions ||
    isLoadingGCKOptions ||
    isLoadingSISICOptions ||
    isLoadingCPMOptions ||
    isFetchReasonResponse
  ) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    )
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" h="100%" w="auto" px="5">
        <MyOfferTable
          userTableProfile={userTableProfile?.data}
          URLSelectedProfileId={profileId ?? ''}
          columnData={{
            salesUserOptions,
            engineerUserOptions,
            creatorOptions: allUserOptions,
            teamOptions,
            businessUnitOptions,
            PMOptions,
            costCenterOptions,
            GCKOptions,
            closeReasons: allReasons.map((reason) => reason.name),
            SISICOptions,
            teamAssistantOptions,
            CPMOptions,
            sourcingUserOptions: sourcingUserOptions,
            gcprLineItemPurchaseScenarioOptions: PURCHASE_SCENARIO_SELECT_OPTIONS,
          }}
        />
      </Box>
    </>
  )
}

export default MyOfferPage
